import React, { useState, useEffect } from 'react';
import { useResponsive } from '../../hooks/useResponsive';

type ScoreInputMode = 'exact' | 'difference';

interface FinalScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** scoreMode viaja al server: "por diferencia" se muestra como "Ganó X por N goles". */
  onSubmit: (team1Goals: number, team2Goals: number, scoreMode: ScoreInputMode) => void;
  isLoading?: boolean;
  team1Name?: string;
  team2Name?: string;
  initialTeam1Goals?: number;
  initialTeam2Goals?: number;
  /** Modo con el que se cargó el resultado actual; al editar se abre en ese mismo modo. */
  initialScoreMode?: 'exact' | 'difference' | null;
}

export const FinalScoreModal: React.FC<FinalScoreModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  team1Name = 'Equipo 1',
  team2Name = 'Equipo 2',
  initialTeam1Goals = 0,
  initialTeam2Goals = 0,
  initialScoreMode
}) => {
  const { isMobile } = useResponsive();
  const [scoreMode, setScoreMode] = useState<ScoreInputMode>('exact');
  const [team1Goals, setTeam1Goals] = useState(initialTeam1Goals);
  const [team2Goals, setTeam2Goals] = useState(initialTeam2Goals);
  
  // Para modo diferencia
  const [winningTeam, setWinningTeam] = useState<'team1' | 'team2' | 'draw'>('team1');
  const [goalDifference, setGoalDifference] = useState(1);

  // Actualizar los estados locales cuando se abre el modal o cambian las props iniciales
  useEffect(() => {
    if (isOpen) {
      setScoreMode(initialScoreMode === 'difference' ? 'difference' : 'exact');
      setTeam1Goals(initialTeam1Goals);
      setTeam2Goals(initialTeam2Goals);

      // Determinar el equipo ganador inicial
      if (initialTeam1Goals > initialTeam2Goals) {
        setWinningTeam('team1');
        setGoalDifference(initialTeam1Goals - initialTeam2Goals);
      } else if (initialTeam2Goals > initialTeam1Goals) {
        setWinningTeam('team2');
        setGoalDifference(initialTeam2Goals - initialTeam1Goals);
      } else {
        setWinningTeam('draw');
        setGoalDifference(0);
      }
    }
  }, [isOpen, initialTeam1Goals, initialTeam2Goals, initialScoreMode]);

  // Calcular goles basados en diferencia
  const calculateGoalsFromDifference = () => {
    if (winningTeam === 'draw') {
      return { team1: 0, team2: 0 };
    } else if (winningTeam === 'team1') {
      return { team1: goalDifference, team2: 0 };
    } else {
      return { team1: 0, team2: goalDifference };
    }
  };

  const handleSubmit = () => {
    let finalTeam1Goals = team1Goals;
    let finalTeam2Goals = team2Goals;
    
    if (scoreMode === 'difference') {
      const calculated = calculateGoalsFromDifference();
      finalTeam1Goals = calculated.team1;
      finalTeam2Goals = calculated.team2;
    }
    
    onSubmit(finalTeam1Goals, finalTeam2Goals, scoreMode);
  };

  if (!isOpen) return null;

  return (
    <div data-testid="final-score-modal" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h2 style={{ 
            color: '#FFFFFF', 
            margin: '0 0 8px 0', 
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: 'bold'
          }}>
            🏆 Resultado Final del Partido
          </h2>
          <p style={{ 
            color: '#B0BEC5', 
            margin: 0, 
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: '1.4'
          }}>
            Sube el resultado final cuando faltan datos de invitados o para corregir el marcador
          </p>
        </div>

        {/* Selector de modo */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <button
              data-testid="final-score-mode-exact"
              onClick={() => setScoreMode('exact')}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: scoreMode === 'exact' ? '#00E676' : '#40444B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              📊 Resultado Exacto
            </button>
            <button
              data-testid="final-score-mode-difference"
              onClick={() => setScoreMode('difference')}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: scoreMode === 'difference' ? '#00E676' : '#40444B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              ⚽ Por Diferencia
            </button>
          </div>
        </div>

        {/* Contenido según el modo seleccionado */}
        {scoreMode === 'exact' ? (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isMobile ? '12px' : '20px',
              marginBottom: '16px'
            }}>
              {/* Equipo 1 */}
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: isMobile ? '12px' : '16px',
                backgroundColor: '#2C2F33',
                borderRadius: '8px',
                border: '1px solid #40444B'
              }}>
                <div style={{ 
                  color: '#FFFFFF', 
                  fontWeight: 'bold', 
                  fontSize: isMobile ? '14px' : '16px',
                  marginBottom: '8px'
                }}>
                  {team1Name}
                </div>
                <input
                  type="number"
                  min="0"
                  value={team1Goals}
                  onChange={(e) => setTeam1Goals(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{
                    width: '60px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #40444B',
                    backgroundColor: '#181A1B',
                    color: '#FFFFFF',
                    fontSize: isMobile ? '16px' : '18px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}
                  disabled={isLoading}
                />
              </div>

              {/* VS */}
              <div style={{
                color: '#B0BEC5',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 'bold',
                padding: '0 8px'
              }}>
                VS
              </div>

              {/* Equipo 2 */}
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: isMobile ? '12px' : '16px',
                backgroundColor: '#2C2F33',
                borderRadius: '8px',
                border: '1px solid #40444B'
              }}>
                <div style={{ 
                  color: '#FFFFFF', 
                  fontWeight: 'bold', 
                  fontSize: isMobile ? '14px' : '16px',
                  marginBottom: '8px'
                }}>
                  {team2Name}
                </div>
                <input
                  type="number"
                  min="0"
                  value={team2Goals}
                  onChange={(e) => setTeam2Goals(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{
                    width: '60px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #40444B',
                    backgroundColor: '#181A1B',
                    color: '#FFFFFF',
                    fontSize: isMobile ? '16px' : '18px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '24px' }}>
            {/* Selector de equipo ganador */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                ¿Quién ganó?
              </div>
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <button
                  data-testid="final-score-winner-team1"
                  onClick={() => setWinningTeam('team1')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: winningTeam === 'team1' ? '#00E676' : '#40444B',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {team1Name}
                </button>
                <button
                  data-testid="final-score-winner-draw"
                  onClick={() => setWinningTeam('draw')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: winningTeam === 'draw' ? '#FFD600' : '#40444B',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Empate
                </button>
                <button
                  data-testid="final-score-winner-team2"
                  onClick={() => setWinningTeam('team2')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: winningTeam === 'team2' ? '#00E676' : '#40444B',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {team2Name}
                </button>
              </div>
            </div>

            {/* Diferencia de goles */}
            {winningTeam !== 'draw' && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Diferencia de goles
                </div>
                <input
                  data-testid="final-score-difference"
                  type="number"
                  min="1"
                  max="20"
                  value={goalDifference}
                  onChange={(e) => setGoalDifference(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #40444B',
                    backgroundColor: '#181A1B',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Vista previa del resultado */}
            <div style={{
              backgroundColor: '#2C2F33',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #40444B',
              textAlign: 'center'
            }}>
              <div style={{
                color: '#B0BEC5',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Resultado:
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#FFFFFF',
                lineHeight: '1.4'
              }}>
                {winningTeam === 'draw' ? (
                  <span style={{ color: '#FFD600' }}>¡Empate!</span>
                ) : winningTeam === 'team1' ? (
                  <span>
                    <span style={{ color: '#00E676' }}>{team1Name}</span>
                    <span style={{ color: '#FFFFFF' }}> gana por </span>
                    <span style={{ color: '#00E676', fontWeight: 'bold' }}>{goalDifference}</span>
                    <span style={{ color: '#FFFFFF' }}> goles</span>
                  </span>
                ) : (
                  <span>
                    <span style={{ color: '#00E676' }}>{team2Name}</span>
                    <span style={{ color: '#FFFFFF' }}> gana por </span>
                    <span style={{ color: '#00E676', fontWeight: 'bold' }}>{goalDifference}</span>
                    <span style={{ color: '#FFFFFF' }}> goles</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Información adicional */}
        <div style={{
          backgroundColor: '#1B5E20',
          borderRadius: '8px',
          padding: '12px',
          border: '1px solid #2E7D32'
        }}>
          <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
            ⚠️ Importante
          </div>
          <div style={{ color: '#B0BEC5', fontSize: '12px', lineHeight: '1.4' }}>
            Este resultado se usará para calcular los resultados (WIN/LOSS/DRAW) de todos los usuarios registrados del partido.
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          marginTop: '20px'
        }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: isMobile ? '10px 16px' : '10px 20px',
              backgroundColor: '#40444B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              minHeight: isMobile ? '44px' : 'auto'
            }}
          >
            Cancelar
          </button>
          <button
            data-testid="final-score-save"
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              padding: isMobile ? '10px 16px' : '10px 20px',
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              minHeight: isMobile ? '44px' : 'auto',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Guardando...' : 'Guardar Resultado'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalScoreModal; 